import { z } from 'zod';
import { moneySchema, type MoneyDto } from '../money';

/**
 * Digitales Kassabuch (Modul accounting): Bareinnahmen/-ausgaben je Monat mit
 * Anfangsbestand, laufendem Saldo und Monatsübertrag. Währung fix EUR.
 */

export const cashBookEntryKindSchema = z.enum(['INCOME', 'EXPENSE']);
export type CashBookEntryKind = z.infer<typeof cashBookEntryKindSchema>;

export const CASH_BOOK_KIND_LABELS: Record<CashBookEntryKind, string> = {
  INCOME: 'Einnahme',
  EXPENSE: 'Ausgabe',
};

/** Betrag im Kassabuch: immer EUR, immer positiv (Richtung steckt in `kind`). */
const cashBookAmountSchema = moneySchema.refine(
  (value) => value.currency === 'EUR' && BigInt(value.amount) > 0n,
  'Betrag muss positiv und in EUR sein.',
);
/** Anfangsbestand: EUR, darf 0 (oder ausnahmsweise negativ) sein. */
const cashBookBalanceSchema = moneySchema.refine(
  (value) => value.currency === 'EUR',
  'Der Kassenstand wird in EUR geführt.',
);

/** Monat initialisieren (Anfangsbestand = Kassenstand am 1. des Monats). */
export const initCashBookMonthSchema = z.object({
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
  openingBalance: cashBookBalanceSchema,
});
export type InitCashBookMonthInput = z.infer<typeof initCashBookMonthSchema>;

/** Anfangsbestand nachträglich korrigieren. */
export const updateCashBookMonthSchema = z.object({
  openingBalance: cashBookBalanceSchema,
});
export type UpdateCashBookMonthInput = z.infer<typeof updateCashBookMonthSchema>;

/** Buchung erfassen (Datum muss im jeweiligen Monat liegen — prüft der Server). */
export const cashBookEntryInputSchema = z.object({
  entryDate: z.string().datetime(),
  kind: cashBookEntryKindSchema,
  amount: cashBookAmountSchema,
  description: z.string().trim().min(1).max(300),
  receiptNumber: z.string().trim().max(60).nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
});
export type CashBookEntryInput = z.infer<typeof cashBookEntryInputSchema>;

export const updateCashBookEntrySchema = cashBookEntryInputSchema.partial();
export type UpdateCashBookEntryInput = z.infer<typeof updateCashBookEntrySchema>;

export const cashBookEntryResponseSchema = z.object({
  id: z.string().uuid(),
  monthId: z.string().uuid(),
  entryDate: z.string().datetime(),
  kind: cashBookEntryKindSchema,
  amount: moneySchema,
  description: z.string(),
  receiptNumber: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type CashBookEntryResponse = z.infer<typeof cashBookEntryResponseSchema>;

export const cashBookMonthResponseSchema = z.object({
  id: z.string().uuid(),
  year: z.number().int(),
  month: z.number().int(),
  openingBalance: moneySchema,
  /** Summe aller Einnahmen des Monats. */
  income: moneySchema,
  /** Summe aller Ausgaben des Monats. */
  expense: moneySchema,
  /** Endbestand = Anfangsbestand + Einnahmen − Ausgaben. */
  closingBalance: moneySchema,
});
export type CashBookMonthResponse = z.infer<typeof cashBookMonthResponseSchema>;

/** GET-Antwort für einen (auch noch nicht initialisierten) Monat. */
export const cashBookMonthDetailSchema = z.object({
  /** null = Monat noch nicht initialisiert. */
  month: cashBookMonthResponseSchema.nullable(),
  entries: z.array(cashBookEntryResponseSchema),
  /** Vorschlag fürs Initialisieren: Endbestand des Vormonats (sonst 0). */
  suggestedOpeningBalance: moneySchema,
});
export type CashBookMonthDetail = z.infer<typeof cashBookMonthDetailSchema>;

// ── Reine Rechen-Helfer (Server, Web, PDF/Excel teilen dieselbe Logik) ─────────

/** Einnahmen-/Ausgaben-Summen einer Buchungsliste (Mikro-Einheiten). */
export function cashBookTotals(
  entries: Array<{ kind: CashBookEntryKind; amount: MoneyDto }>,
): { income: bigint; expense: bigint } {
  let income = 0n;
  let expense = 0n;
  for (const entry of entries) {
    if (entry.kind === 'INCOME') income += BigInt(entry.amount.amount);
    else expense += BigInt(entry.amount.amount);
  }
  return { income, expense };
}

/** Endbestand = Anfangsbestand + Einnahmen − Ausgaben. */
export function cashBookClosingBalance(
  openingBalance: bigint,
  entries: Array<{ kind: CashBookEntryKind; amount: MoneyDto }>,
): bigint {
  const { income, expense } = cashBookTotals(entries);
  return openingBalance + income - expense;
}

/**
 * Buchungen chronologisch sortieren (Datum, dann Erfassungszeitpunkt) und den
 * laufenden Kassenstand NACH jeder Buchung ergänzen — wie im klassischen Kassabuch.
 */
export function withRunningBalance<
  T extends { entryDate: string; createdAt: string; kind: CashBookEntryKind; amount: MoneyDto },
>(openingBalance: bigint, entries: T[]): Array<T & { runningBalance: bigint }> {
  const sorted = [...entries].sort(
    (a, b) => a.entryDate.localeCompare(b.entryDate) || a.createdAt.localeCompare(b.createdAt),
  );
  let balance = openingBalance;
  return sorted.map((entry) => {
    balance += entry.kind === 'INCOME' ? BigInt(entry.amount.amount) : -BigInt(entry.amount.amount);
    return { ...entry, runningBalance: balance };
  });
}

/** Deutsche Monatsnamen (1-basiert, de-DE wie im Reporting) für Auswahl, PDF und Export. */
export const CASH_BOOK_MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
] as const;
